import React from "react";

import get from "lodash/get";
import {
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    FieldError as FormFieldError,
    FieldPath,
    FieldValues,
    UseControllerProps,
    UseFormStateReturn,
} from "react-hook-form";
import { FormControl } from "@mui/material";

import { StyledFormControl } from "@/components/atoms/Form/ControllerInput/styled";

import FieldError from "../FieldError/FieldError";
import InputLabel from "../InputLabel/InputLabel";

export type ControllerInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    render: ({
        field,
        fieldState,
        formState,
        error,
    }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldState: ControllerFieldState;
        formState: UseFormStateReturn<TFieldValues>;
        error: FormFieldError;
    }) => React.ReactElement;
} & UseControllerProps<TFieldValues, TName> & {
        FormControlProps?: React.ComponentProps<typeof FormControl>;
        hideErrorMessage?: boolean;
        required?: boolean;
        fullWidth?: boolean;
        label?: string;
        className?: string;
        LabelProps?: Omit<React.ComponentProps<typeof InputLabel>, "required" | "text">;
    };

const ControllerInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    defaultValue = null,
    render,
    name,
    hideErrorMessage = false,
    FormControlProps,
    label,
    LabelProps,
    fullWidth,
    className,
    required = false,
    ...rest
}: ControllerInputProps<TFieldValues, TName>): React.ReactElement => (
    <Controller
        {...rest}
        name={name}
        defaultValue={defaultValue}
        render={({ field, fieldState, formState }) => {
            const error = get(formState.errors, field.name) as FormFieldError;

            return (
                <StyledFormControl error={!!error} fullWidth={fullWidth} className={className} {...FormControlProps}>
                    {!!label && <InputLabel htmlFor={name} required={required} text={label} {...LabelProps} />}

                    {render({ field, fieldState, formState, error })}

                    {!hideErrorMessage && <FieldError error={error} />}
                </StyledFormControl>
            );
        }}
    />
);

export default ControllerInput;
